import React, { useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableCard } from "../SortableCard";

export const PlayerHand = React.memo(function PlayerHand({
  myHand,
  selected,
  onToggleSelect,
  activeDrag,
  isDealing = false,
  dealingKey = 0,
  spotlight = false, // the redeal vote waits for me: a gold rim round the cards, which rise one by one
}) {
  const isDraggingGroup = activeDrag != null && selected.has(activeDrag.id) && selected.size > 1;

  // Narrow phones: the cards overlap more so the whole hand fits the width. A centred row wider than
  // its box loses its first cards off the left edge, out of reach even by scrolling.
  const rowRef = useRef(null);
  const [fit, setFit] = useState(null); // margin of each card after the first, or null (the classes')
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    const measure = () => {
      const card = row.firstElementChild;
      const n = row.children.length;
      if (!card || n < 2) return setFit(null);
      const cardW = card.offsetWidth;
      const shown = (row.clientWidth - cardW) / (n - 1); // what each card may show of itself
      const usual = cardW - (window.matchMedia("(min-width: 640px)").matches ? 28 : 32); // -space-x-8 / sm:-space-x-7
      setFit(shown < usual ? Math.min(0, Math.max(14, shown) - cardW) : null); // ≥ 14 px: the corner stays readable
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
  }, [myHand.length]);

  // The wave runs once, when the focus starts (not during a deal: the cards are flying in).
  const wave = spotlight && !isDealing && !reducedMotion();

  return (
    <SortableContext items={myHand.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
      <div
        ref={rowRef}
        className={`hand-scroll flex -space-x-8 sm:-space-x-7 max-w-full justify-center mx-auto ${spotlight ? "redeal-hand-glow" : ""}`}
        style={{ justifyContent: "safe center" }}
        data-testid="my-hand"
      >
        {myHand.map((card, idx) => (
          <motion.div
            // La clé inclut dealingKey pour forcer le remontage et rejouer
            // l'animation `initial` à chaque redistribution de cartes.
            key={`${card.id}-${dealingKey}`}
            style={{ zIndex: idx, ...(fit != null && idx > 0 ? { marginLeft: fit } : null) }}
            // The row is click-through (Game.jsx): the cards take the clicks.
            className="shrink-0 pointer-events-auto"
            initial={isDealing ? { rotateY: 90, opacity: 0, scale: 0.85 } : false}
            animate={{ rotateY: 0, opacity: 1, scale: 1, y: wave ? [0, -14, 0] : 0 }}
            transition={{
              delay: isDealing ? idx * 0.09 : 0,
              duration: 0.45,
              ease: [0.34, 1.3, 0.64, 1],
              y: { delay: wave ? 0.35 + idx * 0.055 : 0, duration: 0.6, ease: [0.34, 1.45, 0.64, 1] },
            }}
          >
            <SortableCard
              id={card.id}
              card={card}
              selected={selected.has(card.id)}
              onClick={() => onToggleSelect(card.id)}
              testId={`hand-card-${card.id}`}
              size="md"
              disabled={false}
              ghosted={isDraggingGroup && selected.has(card.id)}
            />
          </motion.div>
        ))}
      </div>
    </SortableContext>
  );
});

function reducedMotion() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

export default PlayerHand;
